import React, { useRef, useCallback, useMemo, useEffect } from "react";
import { observer } from "mobx-react";
import ChatBox from "../Assistance/ChatBox";
import { AssistantWrapper, ChatInputWrapper } from "./UCPChatbox.styled";
import { useStore } from "../../../../../hooks";
import Spinner from "../../../../../components/common/Spinner";
import ChatInput from "../../../../../elements/input/ChatInput";
import AISuggestionsPopup from "../Cards/AISuggestionsPopup";
import NotesDrawerTrigger from "../../../../../elements/v2/NotesDrawer/NotesDrawerTrigger";

const UCPChatbox = observer(() => {
  const {
    authStore,
    unifiedCustomerProfileStore,
    annUnifiedCustomerProfileStore,
    assistanceUCPStore,
    conversationsStore,
    historyAgentStore,
  } = useStore();

  const { runID, threadID } = assistanceUCPStore;
  const { selectedAccount, userData } = authStore;
  const textAreaRef = useRef();
  const suggestionsRef = useRef(null);

  const contactInfo = useMemo(
    () => unifiedCustomerProfileStore.contactInfo[0] || {},
    [unifiedCustomerProfileStore.contactInfo]
  );

  const isAnnUCP = useMemo(
    () => authStore.selectedAccount.metadata.domain === "ANN",
    [authStore.selectedAccount.metadata.domain]
  );

  const ucpData = useMemo(
    () =>
      isAnnUCP ? annUnifiedCustomerProfileStore : unifiedCustomerProfileStore,
    [isAnnUCP, annUnifiedCustomerProfileStore, unifiedCustomerProfileStore]
  );

  useEffect(() => {
    if (selectedAccount?.id) {
      conversationsStore.setCustomerId(selectedAccount.id);
    }
  }, [selectedAccount?.id, conversationsStore]);

  /**
   * Synchronizes the conversation ID between historyAgentStore and assistanceUCPStore
   * 
   * This effect ensures that when a conversation is loaded from history, the same
   * conversation ID is maintained in the assistance store. This allows users to
   * continue existing conversations while preserving the conversation context and
   * maintaining a single conversation record in the history.
   */
  useEffect(() => {
    if (historyAgentStore.selectedConversationId && historyAgentStore.messagesData.length > 0) {
      assistanceUCPStore.setConversationID(historyAgentStore.selectedConversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyAgentStore.selectedConversationId, historyAgentStore.messagesData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        assistanceUCPStore.setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [assistanceUCPStore]);

  const handleSendMessage = useCallback (
    async (message) => {
      await assistanceUCPStore.handleSendMessage(
        message,
        contactInfo,
        selectedAccount,
        isAnnUCP,
        ucpData,
        userData
      );

    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      assistanceUCPStore,
      contactInfo,
      selectedAccount,
      isAnnUCP,
      ucpData,
      userData,
      historyAgentStore,
    ]
  );

  /**
   * Stops the agent response.
   */
  const handleStopAgentResponse = useCallback(() => {
    conversationsStore.setRunId(runID);
    conversationsStore.setThreadId(threadID);
    if (
      assistanceUCPStore.agentResponseSubscription &&
      conversationsStore.runId &&
      conversationsStore.threadId &&
      conversationsStore.customerId
    ) {
      conversationsStore.stopResponse();
      assistanceUCPStore.agentResponseSubscription.unsubscribe();
      assistanceUCPStore.agentResponseSubscription = null;
      assistanceUCPStore.setAgentIsTyping(false);
      assistanceUCPStore.setShowSpinner(false);
      assistanceUCPStore.setIsSendMessageClicked(false);
      assistanceUCPStore.setShowStopButton(false);
    }
  }, [runID, threadID, assistanceUCPStore, conversationsStore]);

  const handleSuggestionSelect = useCallback(
    async (suggestion) => {
      assistanceUCPStore.setNewMessage(suggestion);
      assistanceUCPStore.setShowSuggestions(false);
      await handleSendMessage(suggestion);
    },
    [assistanceUCPStore, handleSendMessage]
  );

  const handleKeyPress = useCallback(
    (e, callback) => {
      if (e.key === "Enter" && !e.shiftKey && !assistanceUCPStore.showSpinner) {
        e.preventDefault();
        handleSendMessage();
        callback("");
      }
    },
    [assistanceUCPStore.showSpinner, handleSendMessage]
  );

  const handleFileUpload = useCallback(
    (attachment) => {
      assistanceUCPStore.setAttachment(attachment);
    },
    [assistanceUCPStore]
  );

  const handleCleanThread = useCallback(() => {
    assistanceUCPStore.cleanupConversation();
  }, [assistanceUCPStore]);

  const handleMessageChange = useCallback(
    (e) => {
      assistanceUCPStore.setNewMessage(e.target.value);
    },
    [assistanceUCPStore]
  );

  const handleCloseSuggestions = useCallback(() => {
    assistanceUCPStore.setShowSuggestions(false);
  }, [assistanceUCPStore]);

  return (
    <AssistantWrapper>
      {assistanceUCPStore.showSpinner && <Spinner className="spinner" />}
      <ChatBox
        messages={assistanceUCPStore.conversationHistory}
        agentIsTyping={assistanceUCPStore.agentIsTyping}
        agentAvatar={assistanceUCPStore?.conciergeAgent?.photo_url}
      />
      <ChatInputWrapper id="chat-wrapper">
        {assistanceUCPStore.showSuggestions && (
          <div ref={suggestionsRef} className="suggestions-container">
            <AISuggestionsPopup
              type={assistanceUCPStore.currentCardType}
              onSelect={handleSuggestionSelect}
              onClose={handleCloseSuggestions}
            />
          </div>
        )}
        <ChatInput
          topRightNotesComponent={<NotesDrawerTrigger />}
          customerId={selectedAccount.id}
          textAreaRef={textAreaRef}
          onChange={handleMessageChange}
          onKeyPress={handleKeyPress}
          showSpinner={assistanceUCPStore.showSpinner}
          newMessage={assistanceUCPStore.newMessage}
          handleSendMessage={handleSendMessage}
          onFileUploaded={handleFileUpload}
          disableSend={
            assistanceUCPStore.isSendMessageClicked ||
            !assistanceUCPStore.conciergeAgent
          }
          onCleanThread={handleCleanThread}
          stop={assistanceUCPStore.showStopButton}
          onStopClick={handleStopAgentResponse}
        />
      </ChatInputWrapper>
    </AssistantWrapper>
  );
});

export default UCPChatbox;
